import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-quotation-history-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    RadioButtonModule,
    ButtonModule,
    TagModule,
  ],
  templateUrl: './quotation-history-dialog.component.html',
  styleUrl: './quotation-history-dialog.component.scss',
})
export class QuotationHistoryDialogComponent implements OnChanges, OnDestroy {
  @Input() quotations: any[] = [];
  @Input() filteredQuotations: any[] = [];
  @Input() selectedQuotationForView: string | null = null;
  @Input() selectedQuotationIndex: number = -1;
  @Input() quotationSearchTerm: string = '';
  @Input() showFooter: boolean = true;

  @Output() quotationSearchTermChange = new EventEmitter<string>();
  @Output() quotationSearch = new EventEmitter<void>();
  @Output() clearSearch = new EventEmitter<void>();
  @Output() quotationSelect = new EventEmitter<string>();
  @Output() editQuotationRequested = new EventEmitter<any>();
  @Output() placeOrderRequested = new EventEmitter<any>();
  @Output() viewQuotationRequested = new EventEmitter<void>();
  @Output() refreshRequested = new EventEmitter<void>();

  /** True between a refresh request and the next quotation list arriving (or a 10s safety timeout). */
  refreshing = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    const listChanged =
      (changes['quotations'] && !changes['quotations'].firstChange) ||
      (changes['filteredQuotations'] && !changes['filteredQuotations'].firstChange);
    if (listChanged) {
      this.stopRefreshing();
    }
  }

  ngOnDestroy(): void {
    this.stopRefreshing();
  }

  /** Called by whoever triggers a reload (this footer or the host dialog's footer) to show the loading state. */
  markRefreshing(): void {
    this.stopRefreshing();
    this.refreshing = true;
    this.refreshTimer = setTimeout(() => this.stopRefreshing(), 10000);
  }

  private stopRefreshing(): void {
    this.refreshing = false;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  onQuotationSearch(): void {
    this.quotationSearchTermChange.emit(this.quotationSearchTerm);
    this.quotationSearch.emit();
  }

  clearQuotationSearch(): void {
    this.quotationSearchTerm = '';
    this.quotationSearchTermChange.emit('');
    this.clearSearch.emit();
  }

  onQuotationSelect(uuid: string): void {
    this.selectedQuotationForView = uuid;
    this.quotationSelect.emit(uuid);
  }

  editQuotation(quotation: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.editQuotationRequested.emit(quotation);
  }

  placeOrderFromQuotation(quotation: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.placeOrderRequested.emit(quotation);
  }

  viewQuotation(): void {
    this.viewQuotationRequested.emit();
  }

  refreshHistoryData(): void {
    this.markRefreshing();
    this.refreshRequested.emit();
  }
}
